/**
 * Money received past a full Mexican issuer, waiting for a decision (owner, 2026-10-21).
 *
 * Per fund: who holds it, for how long, how much, in which product, and what was decided before.
 * When a decision is due the manager offers the return or keeps the money waiting, with a reason.
 * Neither moves money: giving it back is the ordinary withdrawal, started by the client.
 */
import { useCallback, useEffect, useState } from 'react';
import StatusBadge, { type BadgeTone } from '../../components/StatusBadge';
import heldDepositsService, {
  type HeldDeposit,
  type HoldState,
} from '../../services/heldDeposits.service';
import { getApiErrorMessage } from '../../lib/apiError';
import { formatDateTime, formatMoney } from '../../lib/datetime';
import { colors } from '../../styles/theme';
import {
  EVENT_LABEL,
  STATE_LABEL,
  byFund,
  decisionsWaiting,
  eventReason,
  heldFor,
  mayDecide,
  nextDecisionLine,
  reasonProblem,
} from './heldDeposits.helpers';

const TONE: Record<HoldState, BadgeTone> = {
  waiting: 'neutral',
  decision_due: 'warning',
  return_offered: 'info',
  ended: 'success',
};

export default function HeldDepositsSection(): JSX.Element | null {
  const [holds, setHolds] = useState<HeldDeposit[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [flash, setFlash] = useState<string | null>(null);

  const reload = useCallback(async () => {
    setError(null);
    try {
      setHolds(await heldDepositsService.list());
    } catch (e: unknown) {
      setError(getApiErrorMessage(e, 'Could not load the money waiting for a place.'));
    }
  }, []);

  useEffect(() => { void reload(); }, [reload]);

  if (holds.length === 0 && !error) return null;
  const due = holds.filter(mayDecide).length;

  return (
    <section className="mb-8" aria-label="Money waiting for a place">
      <h2 className="text-lg font-semibold mb-1" style={{ color: colors.ink }}>
        Money waiting for a place
      </h2>
      <p className="text-sm mb-3" style={{ color: colors.inkSoft }}>
        This money was received after its fund was full. It is credited to the client and not
        invested. Offering the return tells the client and asks their Account Executive to follow
        up; the client then withdraws it through the usual request and approval.
      </p>
      {due > 0 && (
        <output className="block mb-3 p-3 rounded-lg text-sm"
                style={{ background: colors.warningBg, border: `1px solid ${colors.border}`, color: colors.warning }}>
          {decisionsWaiting(due)}
        </output>
      )}
      {error && (
        <div className="mb-3 p-3 rounded-lg text-sm" role="alert"
             style={{ background: colors.dangerBg, border: `1px solid ${colors.dangerBorder}`, color: colors.danger }}>
          {error}
        </div>
      )}
      {flash && (
        <div className="mb-3 p-3 rounded-lg text-sm"
             style={{ background: colors.successBg, border: `1px solid ${colors.successBorder}`, color: colors.success }}>
          {flash}
        </div>
      )}
      {byFund(holds).map(([entity, rows]) => (
        <div key={entity} className="mb-4">
          <div className="text-xs mb-1" style={{ color: colors.muted }} translate="no">
            {entity}
          </div>
          <ul className="space-y-3">
            {rows.map((hold) => (
              <HoldRow
                key={`${hold.legal_entity_id}-${hold.client_id}`}
                hold={hold}
                onDecided={(message) => { setFlash(message); void reload(); }}
              />
            ))}
          </ul>
        </div>
      ))}
    </section>
  );
}

function HoldRow({
  hold,
  onDecided,
}: Readonly<{ hold: HeldDeposit; onDecided: (message: string) => void }>): JSX.Element {
  const [reason, setReason] = useState('');
  const [keeping, setKeeping] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const offer = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      await heldDepositsService.decide(hold, 'offer_return', null);
      onDecided('The return was offered. The client was told and their Account Executive will follow up.');
    } catch (e: unknown) {
      setError(getApiErrorMessage(e, 'Could not record the decision.'));
    } finally {
      setBusy(false);
    }
  };

  const keepWaiting = async (): Promise<void> => {
    const problem = reasonProblem(reason);
    if (problem) {
      setError(problem);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await heldDepositsService.decide(hold, 'keep_waiting', reason.trim());
      onDecided('The money keeps waiting. The decision will be raised again.');
    } catch (e: unknown) {
      setError(getApiErrorMessage(e, 'Could not record the decision.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <li className="rounded-2xl p-4" style={{ background: colors.surface, border: `1px solid ${colors.border}` }}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="font-semibold" style={{ color: colors.ink }}>
            {hold.client_name} · {formatMoney(hold.amount, hold.currency_code)}
          </div>
          <div className="text-xs mt-0.5" style={{ color: colors.muted }}>
            {hold.product_name}{' · '}{heldFor(hold.days_held)}
            {hold.next_decision_on && <>{' · '}{nextDecisionLine(hold.next_decision_on)}</>}
          </div>
        </div>
        <StatusBadge tone={TONE[hold.state]}>{STATE_LABEL[hold.state]}</StatusBadge>
      </div>

      {hold.history.length > 0 && (
        <ul className="mt-2 text-xs space-y-0.5" style={{ color: colors.inkSoft }}>
          {hold.history.map((event) => (
            <li key={`${event.kind}-${event.cycle}-${event.at}`}>
              {formatDateTime(event.at)}{' · '}{EVENT_LABEL[event.kind]}
              {event.decided_by && <>{' · '}{event.decided_by}</>}
              {eventReason(event) && <>{' · '}{eventReason(event)}</>}
            </li>
          ))}
        </ul>
      )}

      {mayDecide(hold) && (
        <div className="mt-3 pt-3 border-t" style={{ borderColor: colors.border }}>
          {keeping && (
            <label className="block text-xs mb-2" style={{ color: colors.muted }}>
              <span>Why does the money keep waiting?</span>
              <textarea
                value={reason}
                maxLength={500}
                rows={2}
                onChange={(e) => setReason(e.target.value)}
                className="block w-full mt-1 p-2 rounded-md text-sm"
                style={{ border: `1px solid ${colors.border}`, color: colors.ink }}
              />
            </label>
          )}
          {error && (
            <div className="mb-2 text-xs" role="alert" style={{ color: colors.danger }}>{error}</div>
          )}
          <div className="flex gap-2 flex-wrap">
            <button type="button" disabled={busy} onClick={() => void offer()}
                    className="text-xs px-3 py-1.5 rounded-md font-semibold"
                    style={{ background: colors.gold, color: colors.navy }}>
              Offer the return
            </button>
            {keeping ? (
              <button type="button" disabled={busy} onClick={() => void keepWaiting()}
                      className="text-xs px-3 py-1.5 rounded-md font-semibold"
                      style={{ border: `1px solid ${colors.border}`, color: colors.ink }}>
                Confirm: keep waiting
              </button>
            ) : (
              <button type="button" disabled={busy} onClick={() => setKeeping(true)}
                      className="text-xs px-3 py-1.5 rounded-md font-semibold"
                      style={{ border: `1px solid ${colors.border}`, color: colors.ink }}>
                Keep waiting
              </button>
            )}
          </div>
        </div>
      )}
    </li>
  );
}
